import type { TelegramWebApp } from "./telegram/types";

/**
 * Procedural SFX via raw Web Audio API oscillators — no Howler, no audio
 * asset files. Phase 1 priced Howler in on the assumption there'd be real
 * recorded SFX to play; there never were any (same situation as Phase 4's
 * missing HDRI/matcap assets — see crystal.ts). Howler's entire job is
 * managing playback of audio *files*; with nothing to play, it's a
 * dependency for a problem this project doesn't have. Synthesizing tones
 * directly also means there's no "preload + decode all SFX at unlock"
 * step to build — a synthesized tone has nothing to decode.
 */

const MUTE_KEY = "starbot:audio_muted";

let ctx: AudioContext | null = null;
let unlocked = false;
// Default MUTED per the brief: "An app that makes noise unprompted in a
// messenger is a deleted app." Overwritten by loadMutePreference() if the
// user has an explicit saved choice.
let muted = true;

let unavailable = false;

/**
 * PHASE 8 FIX: caught by actually testing against real WebKit (not
 * assumed) — `new AudioContext()` alone threw `ReferenceError: Can't find
 * variable: AudioContext`. Standard vendor-prefix fallback added
 * (`webkitAudioContext` isn't in TS's lib.dom types, accessed via a narrow
 * local cast, not a broad `any`) — real Safari has shipped unprefixed
 * `AudioContext` since 14.1 (2021), so this covers genuinely old WebKit.
 *
 * Returns null, doesn't throw, if no constructor exists under either name
 * — every caller already treats "no sound" as a normal, silent outcome
 * (that's the whole default-muted design), so a missing API should degrade
 * the exact same way a muted preference does, not crash.
 */
function getContext(): AudioContext | null {
  if (unavailable) return null;
  if (!ctx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) {
      unavailable = true;
      return null;
    }
    ctx = new Ctor();
  }
  return ctx;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function isMuted(): boolean {
  return muted || prefersReducedMotion();
}

/**
 * AudioContext starts `suspended` until a user gesture in iOS WKWebView —
 * this resumes it on the first tap anywhere in the app, once, and is safe
 * to call unconditionally on every boot (a no-op after the first real tap).
 */
export function initAudioUnlock(): void {
  const unlock = () => {
    if (unlocked) return;
    unlocked = true;
    const c = getContext();
    if (c?.state === "suspended") {
      c.resume().catch(() => {
        // Some clients refuse resume() outside a "trusted" gesture context
        // even on pointerdown — fails silently, sound just stays off.
      });
    }
    window.removeEventListener("pointerdown", unlock);
  };
  window.addEventListener("pointerdown", unlock, { once: true });
}

export async function loadMutePreference(tg: TelegramWebApp | null): Promise<void> {
  if (!tg?.CloudStorage || !tg.isVersionAtLeast("6.9")) return;
  return new Promise((resolve) => {
    try {
      tg.CloudStorage!.getItem(MUTE_KEY, (err, value) => {
        if (!err && value != null && value !== "") {
          muted = value === "1";
        }
        resolve();
      });
    } catch {
      resolve();
    }
  });
}

export function setMuted(tg: TelegramWebApp | null, value: boolean): void {
  muted = value;
  if (!tg?.CloudStorage || !tg.isVersionAtLeast("6.9")) return;
  try {
    tg.CloudStorage.setItem(MUTE_KEY, value ? "1" : "0");
  } catch {
    // best-effort — the in-memory value above still governs this session
  }
}

export function getMuted(): boolean {
  return muted;
}

/**
 * One short tone: linear attack, exponential decay envelope. Peak gain
 * ~0.09 (~-21dB) per the brief's "-20dB" mixing target. No sub-200Hz
 * content anywhere in this module — "no bass" — every tone below stays in
 * the 400Hz+ range.
 */
function tone(freq: number, durationMs: number, gainPeak: number, type: OscillatorType, delayMs = 0): void {
  if (isMuted()) return;
  const c = getContext();
  if (!c || c.state === "suspended") return; // unavailable, or not unlocked yet — stay silent, don't queue

  const startAt = c.currentTime + delayMs / 1000;
  const duration = durationMs / 1000;

  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.value = freq;

  gain.gain.setValueAtTime(0, startAt);
  gain.gain.linearRampToValueAtTime(gainPeak, startAt + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(startAt);
  osc.stop(startAt + duration + 0.02);
}

/** Tab/selection change — brief, unobtrusive. Paired with hapticSelection(). */
export function playTick(): void {
  tone(900, 45, 0.07, "sine");
}

/** Daily bonus claim. Paired with hapticNotification('success'). */
export function playSuccess(): void {
  tone(700, 55, 0.08, "sine");
  tone(1050, 60, 0.09, "sine", 45);
}

/** Crystal settle-into-place. Paired with hapticImpact('rigid') — a
 * rounder, warmer tone than the bright UI tick, reads as physically
 * solid rather than a notification chime. */
export function playSettle(): void {
  tone(480, 70, 0.09, "triangle");
}

/** Purchase ceremony settle — the biggest moment in the app, so it gets
 * the one three-note tone in the whole module instead of playSuccess's
 * two. Paired with hapticNotification('success'). */
export function playChime(): void {
  tone(660, 70, 0.07, "sine");
  tone(880, 75, 0.08, "sine", 80);
  tone(1320, 140, 0.09, "sine", 165);
}
